import { useContext, useEffect, useMemo, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { AuthContext } from '../../../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { listenToNewsComments } from '../../../../database/newsComments';
import NewsCommentCompose from './NewsCommentCompose';
import NewsCommentItem from './NewsCommentItem';

// Builds a nested tree from the flat comment list. Replies whose parent is no
// longer in the visible set (removed/hidden) are grouped under a single synthetic
// placeholder so their thread structure survives instead of scattering to the top.
const buildTree = (rows) => {
  const byId = new Map();
  rows.forEach((r) => byId.set(r.id, { ...r, replies: [] }));

  const placeholders = new Map();
  const getPlaceholder = (id) => {
    if (!placeholders.has(id)) placeholders.set(id, { id, placeholder: true, replies: [] });
    return placeholders.get(id);
  };

  const roots = [];
  byId.forEach((node) => {
    const pid = node.parentId;
    if (pid && byId.has(pid)) byId.get(pid).replies.push(node);
    else if (pid) getPlaceholder(pid).replies.push(node);
    else roots.push(node);
  });
  placeholders.forEach((p) => roots.push(p));
  return roots;
};

const NewsComments = ({ newsId }) => {
  const { currentUser, hasPermission } = useContext(AuthContext);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  const canModerate = hasPermission('news.manage') || hasPermission('profileComments.moderate');
  const currentUid = currentUser?.uid || null;

  useEffect(() => {
    if (!newsId) return undefined;
    setLoading(true);
    return listenToNewsComments(
      newsId,
      (rows) => {
        setComments(rows);
        setLoading(false);
      },
      { canModerate, currentUid }
    );
  }, [newsId, canModerate, currentUid]);

  const tree = useMemo(() => buildTree(comments), [comments]);
  const count = comments.filter((c) => c.status !== 'removed').length;

  return (
    <Card className="mt-12 border-border/60 bg-background/40">
      <CardHeader>
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          <MessageSquare className="h-3 w-3" />
          Discussion
        </div>
        <CardTitle className="font-display text-lg">
          {loading ? 'Comments' : `Comments · ${count}`}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <NewsCommentCompose newsId={newsId} />
        {loading ? (
          <div className="h-20 animate-pulse rounded-md bg-muted/40" />
        ) : tree.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No comments yet. Be the first to start the conversation.
          </p>
        ) : (
          <ul className="space-y-5">
            {tree.map((node) => (
              <NewsCommentItem
                key={node.id}
                comment={node}
                newsId={newsId}
                canModerate={canModerate}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default NewsComments;
